import { useTheme } from "@/context/ThemeContext";
import { Sun, Moon, Monitor } from "lucide-react";
import { toast } from "sonner";

// v77.20.1 — Quick three-state theme toggle for the user menu.
//
// Cycles: system → light → dark → system → ...
// Uses the same `useTheme` context as the Settings AppearanceCard so
// changes are persisted to the server and reflected across the app
// instantly. Designed to be tiny and visually quiet so it can sit
// alongside the existing sidebar Sign-out button without dominating it.

const ICON_FOR = { system: Monitor, light: Sun, dark: Moon };
const NEXT_FOR = { system: "light", light: "dark", dark: "system" };
const LABEL_FOR = {
  system: "Use device setting",
  light: "Light mode",
  dark: "Dark mode",
};

export function ThemeToggle({ className = "", variant = "icon" }) {
  const { preference, setPreference } = useTheme();
  const Icon = ICON_FOR[preference] || Monitor;
  const next = NEXT_FOR[preference] || "system";

  const cycle = async () => {
    await setPreference(next);
    toast.success(LABEL_FOR[next], { duration: 1600 });
  };

  if (variant === "row") {
    // Full-width row style — used in the mobile More sheet.
    return (
      <button
        type="button"
        onClick={cycle}
        data-testid="theme-toggle-row"
        className={`w-full flex items-center gap-3 px-4 py-3 border border-[#D9E1E3] bg-white text-[#2E3A3F] hover:bg-[#EEF3F4] hover:border-[#BFCCD0] rounded-md text-sm transition-colors min-h-[48px] ${className}`}
      >
        <Icon className="w-5 h-5 flex-shrink-0 text-[#1F9FA4]" strokeWidth={1.6} />
        <span className="font-medium">Appearance</span>
        <span className="ml-auto text-[10px] uppercase tracking-[0.1em] text-[#6F828A]">
          {LABEL_FOR[preference]}
        </span>
      </button>
    );
  }

  // Compact icon button — used in the desktop sidebar footer.
  return (
    <button
      type="button"
      onClick={cycle}
      data-testid="theme-toggle"
      title={`Appearance: ${LABEL_FOR[preference]} — tap to switch`}
      aria-label={`Appearance: ${LABEL_FOR[preference]}. Tap to cycle to ${LABEL_FOR[next]}.`}
      className={`inline-flex items-center justify-center w-8 h-8 rounded-sm text-[#6F828A] hover:bg-[#EEF3F4] hover:text-[#2E3A3F] transition-colors ${className}`}
    >
      <Icon className="w-4 h-4" strokeWidth={1.6} />
    </button>
  );
}

export default ThemeToggle;
